import React, { Component } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Box,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { baseTheme } from "mui-theme";

import { toast } from "react-toastify";
import { diff } from "deep-object-diff";
import { Formik } from "formik";
import * as Yup from "yup";

import { validateRTFormula } from "App/utils/formulaValidator";
import { YieldApi } from "api/yield";
import { ConfigService } from "App/services/config";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import Popup from "reusable-components/popup/popup";
import { ConfigApi } from "api/config";

interface IEditRoomTypePanelProps {
  isNew: boolean;
  roomType: string;
  user: any;
  hotel: any;
  onclose: () => void;
  onUpdate: (rt: string) => void;
  unEditable: boolean;
  allData: any;
  counter: number;
}

interface State {
  data: any;
  disableButton: boolean;
  isCreateRtDialogOpened: boolean;
  pendingRoomValues?: any;
  pendingFormikActions?: any;
}

class EditRoomPanel extends Component<IEditRoomTypePanelProps> {
  readonly state: State = {
    data: {
      shortDescription: "",
      id: "",
      minRate: "",
      maxRate: "",
      occupancy: "",
      childOccupancy: "",
      inventory: "",
      formula: "",
      sortOrder: 0,
      DOWVariation: {
        enabled: false,
        monday: "",
        tuesday: "",
        wednesday: "",
        thursday: "",
        friday: "",
        saturday: "",
        sunday: "",
      },
      DOWStopSell: {
        enabled: false,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
      },
    },
    disableButton: false,
    isCreateRtDialogOpened: false,
    pendingRoomValues: undefined,
    pendingFormikActions: undefined,
  };
  allData: any;

  constructor(props: any) {
    super(props);
    this.state.data = this.props.roomType;
    this.allData = this.props.allData;
  }

  // Handle close button
  handleOnClose = () => {
    this.props.onclose();
  };

  // Create a new room type using Formik
  createRoomType = (values: any, actions: any) => {
    const hotel = this.props.hotel;
    const rt = { ...values }; // Copy values to avoid reference issues

    if (rt.isManual) {
      delete rt.formula;
    } else {
      delete rt.defaultRate;
    }

    rt.sortOrder = Object.keys(this.props.hotel.roomTypes).length + 1;
    rt.id = rt.id || getNewrtId(this.props.hotel, rt);
    rt.hotelId = hotel.hotelId;

    // Set state to disable button
    this.setState({ disableButton: true });
    this.props.onclose();

    // Create promise to handle API calls
    const createAndUpdatePromise = async () => {
      await ConfigApi.writeRoomType(rt);
      await ConfigService.revanista.hotel.updateConfig(hotel.hotelId);
      return rt;
    };

    // Execute the promise with toast notifications
    toast
      .promise(createAndUpdatePromise(), {
        pending: "Creating Room Type '" + rt.shortDescription + "'",
        success: "Room Type created successfully",
        error: "Room Type creation failed",
      })
      .then((result) => {
        this.setState({ disableButton: false });
        this.props.onUpdate(result);
      })
      .catch(() => {
        this.setState({ disableButton: false });
        actions.setSubmitting(false);
      });
  };

  // Update an existing room type using Formik
  updateRoomType = (values: any, actions: any) => {
    const hotel = this.props.hotel;
    const room = { ...values }; // Copy values to avoid reference issues

    // Set childOccupancy to 0 if null
    if (room.childOccupancy == null) {
      room.childOccupancy = "0";
    }

    // Ensure sortOrder is set when updating
    if (!room.sortOrder && hotel.roomTypes[room.id]) {
      room.sortOrder = hotel.roomTypes[room.id].sortOrder || 0;
    }

    // Compare with original room data to detect changes
    const difs = diff(
      room,
      JSON.parse(JSON.stringify(hotel.roomTypes[room.id])),
    );

    // If no changes, notify user and close panel
    if (Object.keys(difs).length === 0) {
      toast.warn("No change detected");
      this.props.onclose();
      actions.setSubmitting(false);
      return;
    }

    // Add hotel ID
    room.hotelId = hotel.hotelId;

    // Set state to disable button during update
    this.setState({ disableButton: true });
    this.props.onclose();

    // Create promise to handle API calls
    const updateAndConfigPromise = async () => {
      await ConfigApi.writeRoomType(room);
      await ConfigService.revanista.hotel.updateConfig(hotel.hotelId);
      return room;
    };

    // Execute the promise with toast notifications
    toast
      .promise(updateAndConfigPromise(), {
        pending:
          "Room Type - '" + room.shortDescription + "' update in progress...",
        success:
          "Room Type - '" + room.shortDescription + "' updated successfully.",
        error:
          "Room Type - '" + room.shortDescription + "' modification failed!",
      })
      .then(() => {
        this.setState({ disableButton: false });
        actions.setSubmitting(false);
      })
      .catch(() => {
        this.setState({ disableButton: false });
        actions.setSubmitting(false);
      });
  };

  render() {
    const rtData = this.state.data;
    const isAdminUser = hasPermission(ACTIONS.EDIT_ROOM);

    // Validation schema for room type form
    const formulaTest = Yup.string().test(
      "is-valid-formula",
      "Invalid formula",
      (value) => !value || validateRTFormula(value),
    );

    const validationSchema = Yup.object().shape({
      shortDescription: Yup.string()
        .label("Room Type Name")
        .required("Mandatory field"),
      id: Yup.string()
        .label("Room Type ID")
        .required("Mandatory field")
        .test("is-id-unique", "Room Type ID already exists", (value) => {
          if (this.props.isNew) {
            const roomType = this.props.hotel.roomTypes[value];
            return !roomType || roomType.deleted;
          }
          // For existing room type, we don't check uniqueness
          return true;
        }),
      minRate: Yup.number()
        .label("Minimum Rate")
        .positive()
        .required("Mandatory field")
        .test(
          "less-than-max",
          "Min rate can't be greater than or equal to the max rate",
          function (value) {
            const { maxRate } = this.parent;
            return !value || !maxRate || Number(value) < Number(maxRate);
          },
        ),
      maxRate: Yup.number()
        .label("Maximum Rate")
        .positive()
        .required("Mandatory field")
        .test(
          "more-than-min",
          "Max rate can't be smaller than or equal to the min rate",
          function (value) {
            const { minRate } = this.parent;
            return !value || !minRate || Number(value) > Number(minRate);
          },
        ),
      occupancy: Yup.number()
        .label("Occupancy")
        .positive()
        .integer()
        .required("Mandatory field"),
      childOccupancy: Yup.number()
        .label("Child Occupancy")
        .min(0)
        .integer()
        .required("Mandatory field"),
      inventory: Yup.number()
        .label("Inventory")
        .positive()
        .required("Mandatory field"),
      formula: formulaTest.required("Mandatory field"),
      DOWVariation: Yup.object().shape({
        enabled: Yup.boolean(),
        monday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        tuesday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        wednesday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        thursday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        friday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        saturday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
        sunday: Yup.string().when("enabled", {
          is: true,
          then: () => formulaTest.required("Mandatory field"),
        }),
      }),
      DOWStopSell: Yup.object().shape({
        enabled: Yup.boolean(),
        monday: Yup.boolean(),
        tuesday: Yup.boolean(),
        wednesday: Yup.boolean(),
        thursday: Yup.boolean(),
        friday: Yup.boolean(),
        saturday: Yup.boolean(),
        sunday: Yup.boolean(),
      }),
    });

    return (
      <ThemeProvider theme={baseTheme}>
        <FormControl style={{ display: "inline-block", width: "100%" }}>
          {this.props.isNew && (
            <div style={{ marginLeft: 25, fontSize: 20 }}>
              <b>New Room Type</b>
            </div>
          )}
          {!this.props.isNew && (
            <div style={{ marginLeft: 25, fontSize: 20 }}>
              <b>Edit Room Plan:</b>{" "}
              <span style={{ color: "#d93025" }}>
                {rtData.shortDescription}
              </span>{" "}
              ({rtData.id})
            </div>
          )}
          <br />
          <Formik
            initialValues={{
              shortDescription: rtData.shortDescription || "",
              id: rtData.id || "",
              minRate: rtData.minRate || "",
              maxRate: rtData.maxRate || "",
              occupancy: rtData.occupancy || "",
              childOccupancy:
                rtData.childOccupancy !== undefined
                  ? rtData.childOccupancy
                  : "",
              inventory: rtData.inventory || "",
              formula: rtData.formula || "",
              sortOrder: rtData.sortOrder || 0,
              DOWVariation: {
                enabled: rtData.DOWVariation?.enabled || false,
                monday: rtData.DOWVariation?.monday || "",
                tuesday: rtData.DOWVariation?.tuesday || "",
                wednesday: rtData.DOWVariation?.wednesday || "",
                thursday: rtData.DOWVariation?.thursday || "",
                friday: rtData.DOWVariation?.friday || "",
                saturday: rtData.DOWVariation?.saturday || "",
                sunday: rtData.DOWVariation?.sunday || "",
              },
              DOWStopSell: {
                enabled: rtData.DOWStopSell?.enabled || false,
                monday: rtData.DOWStopSell?.monday || false,
                tuesday: rtData.DOWStopSell?.tuesday || false,
                wednesday: rtData.DOWStopSell?.wednesday || false,
                thursday: rtData.DOWStopSell?.thursday || false,
                friday: rtData.DOWStopSell?.friday || false,
                saturday: rtData.DOWStopSell?.saturday || false,
                sunday: rtData.DOWStopSell?.sunday || false,
              },
            }}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={(values, actions) => {
              // Open confirmation dialog and store values; actual write occurs after confirm
              this.setState({
                isCreateRtDialogOpened: true,
                pendingRoomValues: values,
                pendingFormikActions: actions,
              });
              // Do not setSubmitting(false) yet; we keep the dialog flow consistent
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              submitForm,
            }) => {
              const formContent = (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ "& .MuiTextField-root": { m: 1, width: "25ch" } }}>
                    <div>
                      {this.props.isNew && (
                        <div
                          style={{
                            marginLeft: 4,
                            fontSize: 18,
                            marginBottom: 8,
                          }}
                        >
                          <b>New Room Type</b>
                        </div>
                      )}
                      {!this.props.isNew && (
                        <div
                          style={{
                            marginLeft: 4,
                            fontSize: 18,
                            marginBottom: 8,
                          }}
                        >
                          <b>Edit Room Type:</b>{" "}
                          <span style={{ color: "#d93025" }}>
                            {rtData.shortDescription}
                          </span>{" "}
                          ({rtData.id})
                        </div>
                      )}
                      <TextField
                        fullWidth
                        style={{ marginTop: 20, marginBottom: 24 }}
                        id="outlined-basic"
                        label="Room Type Name"
                        variant="outlined"
                        name="shortDescription"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.shortDescription}
                        disabled={!isAdminUser}
                        error={
                          touched.shortDescription &&
                          Boolean(errors.shortDescription)
                        }
                        helperText={
                          touched.shortDescription &&
                          (errors.shortDescription as string)
                        }
                      />
                      <TextField
                        fullWidth
                        style={{ marginTop: 20, marginBottom: 24 }}
                        id="outlined-basic"
                        label="ID"
                        variant="outlined"
                        name="id"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.id}
                        disabled={
                          !this.props.isNew &&
                          (this.props.hotel.info?.level == "Pro"
                            ? !hasPermission(ACTIONS.MANAGE_PRO_HOTEL)
                            : !hasPermission(ACTIONS.ADD_ROOM))
                        }
                        error={touched.id && Boolean(errors.id)}
                        helperText={touched.id && (errors.id as string)}
                      />
                      <TextField
                        fullWidth
                        type="Number"
                        style={{ marginTop: 20, marginBottom: 24 }}
                        name="inventory"
                        value={values.inventory}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Number of Rooms"
                        variant="outlined"
                        error={touched.inventory && Boolean(errors.inventory)}
                        helperText={
                          touched.inventory && (errors.inventory as string)
                        }
                      />
                    </div>
                    <div>
                      {!values.DOWVariation.enabled && (
                        <TextField
                          fullWidth
                          style={{ marginTop: 20, marginBottom: 24 }}
                          name="formula"
                          value={values.formula}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!isAdminUser}
                          label="Formula"
                          variant="outlined"
                          error={touched.formula && Boolean(errors.formula)}
                          helperText={
                            touched.formula && (errors.formula as string)
                          }
                        />
                      )}
                      <TextField
                        fullWidth
                        style={{ marginTop: 20, marginBottom: 24 }}
                        type="Number"
                        name="minRate"
                        value={values.minRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Minimum Rate"
                        variant="outlined"
                        error={touched.minRate && Boolean(errors.minRate)}
                        helperText={
                          touched.minRate && (errors.minRate as string)
                        }
                      />
                      <TextField
                        fullWidth
                        style={{ marginTop: 20, marginBottom: 24 }}
                        type="Number"
                        name="maxRate"
                        value={values.maxRate}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Maximum Rate"
                        variant="outlined"
                        error={touched.maxRate && Boolean(errors.maxRate)}
                        helperText={
                          touched.maxRate && (errors.maxRate as string)
                        }
                      />
                    </div>
                    <div>
                      <TextField
                        fullWidth
                        style={{ marginTop: 20, marginBottom: 24 }}
                        type="Number"
                        name="occupancy"
                        value={values.occupancy}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Occupancy"
                        variant="outlined"
                        error={touched.occupancy && Boolean(errors.occupancy)}
                        helperText={
                          touched.occupancy && (errors.occupancy as string)
                        }
                      />
                      <TextField
                        fullWidth
                        type="Number"
                        style={{ marginTop: 20, marginBottom: 24 }}
                        name="childOccupancy"
                        value={values.childOccupancy}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={!isAdminUser}
                        label="Child Occupancy"
                        variant="outlined"
                        error={
                          touched.childOccupancy &&
                          Boolean(errors.childOccupancy)
                        }
                        helperText={
                          touched.childOccupancy &&
                          (errors.childOccupancy as string)
                        }
                      />
                    </div>
                    <FormControlLabel
                      style={{ marginTop: 20, marginBottom: 24 }}
                      control={
                        <Checkbox
                          checked={values.DOWVariation.enabled}
                          onChange={(e) => {
                            setFieldValue(
                              "DOWVariation.enabled",
                              e.target.checked,
                            );
                          }}
                          disabled={!isAdminUser}
                          name="DOWVariation.enabled"
                          color="primary"
                          id="dow-variation"
                        />
                      }
                      label="Day of week variation"
                    />
                    <FormControlLabel
                      style={{ marginTop: 20, marginBottom: 24 }}
                      control={
                        <Checkbox
                          checked={values.DOWStopSell.enabled}
                          onChange={(e) => {
                            setFieldValue(
                              "DOWStopSell.enabled",
                              e.target.checked,
                            );
                          }}
                          disabled={!isAdminUser}
                          name="DOWStopSell.enabled"
                          color="primary"
                          id="dow-stop-sell"
                        />
                      }
                      label="Day of week stop sell"
                    />
                    {values.DOWStopSell.enabled && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ fontSize: 20, marginBottom: "8px" }}>
                          <b>Day of week stop sell</b>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "4px",
                          }}
                        >
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.monday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.monday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.monday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Mon"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.tuesday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.tuesday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.tuesday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Tue"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.wednesday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.wednesday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.wednesday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Wed"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.thursday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.thursday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.thursday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Thu"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.friday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.friday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.friday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Fri"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.saturday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.saturday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.saturday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Sat"
                          />
                          <FormControlLabel
                            style={{ margin: 0, minWidth: "auto" }}
                            control={
                              <Checkbox
                                checked={values.DOWStopSell.sunday}
                                onChange={(e) => {
                                  setFieldValue(
                                    "DOWStopSell.sunday",
                                    e.target.checked,
                                  );
                                }}
                                disabled={!isAdminUser}
                                name="DOWStopSell.sunday"
                                color="primary"
                                size="small"
                              />
                            }
                            label="Sun"
                          />
                        </div>
                      </div>
                    )}
                    {values.DOWVariation.enabled && (
                      <div style={{ marginTop: "8px" }}>
                        <div style={{ fontSize: 20, marginBottom: "8px" }}>
                          <b>Day of week variation</b>
                        </div>
                        <div>
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.monday"
                            label="Mon"
                            variant="outlined"
                            value={values.DOWVariation.monday}
                            error={
                              touched.DOWVariation?.monday &&
                              Boolean(errors.DOWVariation?.monday)
                            }
                            helperText={
                              touched.DOWVariation?.monday &&
                              (errors.DOWVariation?.monday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.tuesday"
                            label="Tue"
                            variant="outlined"
                            value={values.DOWVariation.tuesday}
                            error={
                              touched.DOWVariation?.tuesday &&
                              Boolean(errors.DOWVariation?.tuesday)
                            }
                            helperText={
                              touched.DOWVariation?.tuesday &&
                              (errors.DOWVariation?.tuesday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.wednesday"
                            label="Wed"
                            variant="outlined"
                            value={values.DOWVariation.wednesday}
                            error={
                              touched.DOWVariation?.wednesday &&
                              Boolean(errors.DOWVariation?.wednesday)
                            }
                            helperText={
                              touched.DOWVariation?.wednesday &&
                              (errors.DOWVariation?.wednesday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.thursday"
                            label="Thu"
                            variant="outlined"
                            value={values.DOWVariation.thursday}
                            error={
                              touched.DOWVariation?.thursday &&
                              Boolean(errors.DOWVariation?.thursday)
                            }
                            helperText={
                              touched.DOWVariation?.thursday &&
                              (errors.DOWVariation?.thursday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.friday"
                            label="Fri"
                            variant="outlined"
                            value={values.DOWVariation.friday}
                            error={
                              touched.DOWVariation?.friday &&
                              Boolean(errors.DOWVariation?.friday)
                            }
                            helperText={
                              touched.DOWVariation?.friday &&
                              (errors.DOWVariation?.friday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.saturday"
                            label="Sat"
                            variant="outlined"
                            value={values.DOWVariation.saturday}
                            error={
                              touched.DOWVariation?.saturday &&
                              Boolean(errors.DOWVariation?.saturday)
                            }
                            helperText={
                              touched.DOWVariation?.saturday &&
                              (errors.DOWVariation?.saturday as string)
                            }
                          />
                          <TextField
                            style={{ marginBottom: 8, minWidth: 60 }}
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={!isAdminUser}
                            name="DOWVariation.sunday"
                            label="Sun"
                            variant="outlined"
                            value={values.DOWVariation.sunday}
                            error={
                              touched.DOWVariation?.sunday &&
                              Boolean(errors.DOWVariation?.sunday)
                            }
                            helperText={
                              touched.DOWVariation?.sunday &&
                              (errors.DOWVariation?.sunday as string)
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Box>
                </form>
              );

              return (
                <Popup
                  isPopupOpen={true}
                  closeOnOutsideClick={true}
                  title={`${this.props.isNew ? "Create" : "Edit"} Room Type`}
                  content={[formContent]}
                  action={() => submitForm()}
                  actionName="Save"
                  cancelAction={this.props.onclose}
                  cancelName={isAdminUser ? "Cancel" : "Close"}
                  actionDisabled={this.state.disableButton || isSubmitting}
                  hideAction={!isAdminUser}
                  maxWidth="md"
                />
              );
            }}
          </Formik>
          {/* Confirmation Popup after submit */}
          <Popup
            isPopupOpen={this.state.isCreateRtDialogOpened}
            closeOnOutsideClick={true}
            title={`${this.props.isNew ? "Create" : "Edit"} ${
              this.state.data?.shortDescription || "Room Type"
            }`}
            content={[
              `Upon ${
                this.props.isNew ? "creation" : "modification"
              } of a room type, please review the adjustments on the yield sheet afterwards to ensure everything is correct.`,
            ]}
            action={() => {
              const pending = this.state.pendingRoomValues;
              const actions = this.state.pendingFormikActions;
              if (!pending || !actions) {
                this.setState({ isCreateRtDialogOpened: false });
                return;
              }
              if (this.props.isNew) {
                this.createRoomType(pending, actions);
              } else {
                this.updateRoomType(pending, actions);
              }
              this.setState({
                isCreateRtDialogOpened: false,
                pendingRoomValues: undefined,
                pendingFormikActions: undefined,
              });
            }}
            actionName={`${this.props.isNew ? "Create" : "Edit"} Room Type`}
            cancelAction={() =>
              this.setState({
                isCreateRtDialogOpened: false,
                pendingRoomValues: undefined,
                pendingFormikActions: undefined,
              })
            }
            cancelName="Cancel"
          />
        </FormControl>
      </ThemeProvider>
    );
  }
}
export default EditRoomPanel;

// Helper function to generate a new room type ID
const getNewrtId = (hotel: any, rtData: any): string => {
  let id = rtData.id;
  if (!id) {
    id = rtData.shortDescription.replace(/\s/g, "_").toLowerCase();
    let suffixIdx = 0;
    while (hotel.roomTypes[id + (suffixIdx ? "_" + suffixIdx : "")]) {
      suffixIdx++;
    }
    id = id + (suffixIdx ? "_" + suffixIdx : "");
  }
  return id;
};
